import React, { useState } from 'react';
import { Info, Image } from 'lucide-react';
import UatArchitectureImage from '../assets/images/uat-architecture.png';

const UatEnvironmentInfo = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showDiagram, setShowDiagram] = useState(false);

  const handleLearnMoreClick = () => {
    setIsOpen(!isOpen);
    setShowDiagram(false);
  };

  const handleDiagramClick = () => {
    setShowDiagram(!showDiagram);
    setIsOpen(false);
  };

  return (
    <div className="ml-auto flex gap-2">
      <button
        onClick={handleLearnMoreClick}
        style={{ backgroundColor: '#28A745', color: '#FFFFFF' }}
        className="flex items-center justify-center gap-1 w-custom px-3 py-3 rounded-lg hover:opacity-90"
      >
        <Info size={16} />
        Learn More
      </button>
      <button
        onClick={handleDiagramClick}
        style={{ backgroundColor: '#6F42C1', color: '#FFFFFF' }}
        className="flex items-center justify-center gap-1 w-custom px-3 py-3 rounded-lg hover:opacity-90"
      >
        <Image size={16} />
        Diagram
      </button>
      
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-xl p-8 m-4 max-w-5xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h3 className="text-2xl font-semibold text-gray-900 mb-2">UAT Environment Options</h3>
                <p className="text-gray-600">Choose the best UAT deployment strategy for your needs</p>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>
            </div>

            <div className="grid grid-cols-2 gap-6 mb-6">
              {/* Combined Environment */}
              <div className="border rounded-lg p-6 bg-gray-50">
                <h4 className="text-lg font-semibold mb-2">Combined Environment</h4>
                <p className="text-sm text-gray-600 mb-4">UAT and Production on same server</p>
                
                <div className="space-y-4">
                  <div className="bg-green-50 border border-green-100 rounded-lg p-4">
                    <h5 className="text-md font-medium text-green-900 mb-2">Benefits</h5>
                    <ul className="space-y-2 text-sm text-green-800">
                      <li>• Lower infrastructure costs</li>
                      <li>• Simplified management</li>
                      <li>• Reduced operational overhead</li>
                      <li>• Faster deployment between environments</li>
                    </ul>
                  </div>

                  <div className="bg-red-50 border border-red-100 rounded-lg p-4">
                    <h5 className="text-md font-medium text-red-900 mb-2">Limitations</h5>
                    <ul className="space-y-2 text-sm text-red-800">
                      <li>• Resource contention possible</li>
                      <li>• Maintenance affects both environments</li>
                      <li>• May not meet some compliance requirements</li>
                      <li>• Limited environment isolation</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Dedicated Environment */}
              <div className="border rounded-lg p-6">
                <h4 className="text-lg font-semibold mb-2">Dedicated Environment</h4>
                <p className="text-sm text-gray-600 mb-4">Separate UAT server</p>
                
                <div className="space-y-4">
                  <div className="bg-green-50 border border-green-100 rounded-lg p-4">
                    <h5 className="text-md font-medium text-green-900 mb-2">Benefits</h5>
                    <ul className="space-y-2 text-sm text-green-800">
                      <li>• Complete environment isolation</li>
                      <li>• Independent maintenance windows</li>
                      <li>• Better resource management</li>
                      <li>• Enhanced security compliance</li>
                    </ul>
                  </div>

                  <div className="bg-amber-50 border border-amber-100 rounded-lg p-4">
                    <h5 className="text-md font-medium text-amber-900 mb-2">Considerations</h5>
                    <ul className="space-y-2 text-sm text-amber-800">
                      <li>• Additional infrastructure costs</li>
                      <li>• More complex management</li>
                      <li>• Increased operational overhead</li>
                      <li>• Additional backup requirements</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
              <h4 className="text-lg font-medium text-blue-900 mb-2">When to Choose Dedicated UAT</h4>
              <ul className="space-y-2 text-sm text-blue-800">
                <li>• Your application requires frequent testing cycles</li>
                <li>• You need to meet specific compliance requirements</li>
                <li>• Production performance is critical and can't risk resource contention</li>
                <li>• You need different maintenance windows for UAT and Production</li>
                <li>• Your budget allows for additional infrastructure costs</li>
              </ul>
            </div>
          </div>
        </div>
      )}

{showDiagram && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-xl p-8 m-4 max-w-5xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-6">
              <h3 className="text-2xl font-semibold text-gray-900">UAT Architecture Diagram</h3>
              <button
                onClick={() => setShowDiagram(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>
            </div>
            <img 
              src={UatArchitectureImage} 
              alt="UAT Architecture Diagram" 
              className="w-full h-auto"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default UatEnvironmentInfo;